import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import type { ApiError, ApiErrorResponse } from "src/features/axios/axios.types";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import employeeService, { ResetPasswordsResponse } from "../employeeService";
import RegisterHeader from "src/features/organization/components/RegisterHeader";

const uploadSchema = z.object({
  file: z
    .any()
    .refine(
      (file) =>
        file?.[0]?.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      {
        message: "Only Excel files are allowed.",
      }
    ),
});

type UploadFormSchema = z.infer<typeof uploadSchema>;

const pageContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minHeight: "100vh",
  maxWidth: "100vw",
};

const pageContentStyle: React.CSSProperties = {
  flexDirection: "column",
  flex: 6,
  padding: "0 10px 10px 10px",
  minWidth: 0,
  width: "100%",
  alignItems: "center",
  display: "flex",
};

const registrationWindowContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "70%",
  maxWidth: "600px",
  marginTop: "20px",
};

const formCardStyle: React.CSSProperties = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  borderRadius: "10px",
  backgroundColor: "var(--clearpath-dark-gray)",
  minHeight: "400px",
  padding: "30px",
};

const headerStyle: React.CSSProperties = {
  fontSize: "24px",
  fontWeight: "bold",
  marginBottom: "24px",
  color: "var(--clearpath-yellow)",
  textAlign: "center",
};

const labelStyle: React.CSSProperties = {
  fontSize: "14px",
  color: "var(--muted-text-gray)",
  fontWeight: 500,
  marginBottom: "8px",
  display: "block",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px",
  backgroundColor: "var(--clearpath-dark-gray)",
  borderRadius: "10px",
  border: "1px solid #4b4b4b",
  color: "white",
  fontSize: "14px",
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  border: "1px solid #f40202",
};

const buttonStyle: React.CSSProperties = {
  background:
    "linear-gradient(180deg, #fec110 0%, #fec110 0.01%, #cc9800 100%)",
  color: "var(--clearpath-dark-gray)",
  padding: "12px 24px",
  borderRadius: "10px",
  border: "none",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: "14px",
  width: "100%",
  marginTop: "16px",
};

const buttonDisabledStyle: React.CSSProperties = {
  ...buttonStyle,
  opacity: 0.5,
  cursor: "not-allowed",
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "linear-gradient(180deg, #4CAF50 0%, #388E3C 100%)",
  color: "white",
};

const successBoxStyle: React.CSSProperties = {
  marginTop: "16px",
  padding: "16px",
  borderRadius: "10px",
  backgroundColor: "rgba(76, 175, 80, 0.2)",
  border: "1px solid #4CAF50",
  width: "100%",
};

const errorBoxStyle: React.CSSProperties = {
  marginTop: "16px",
  padding: "16px",
  borderRadius: "10px",
  backgroundColor: "rgba(244, 67, 54, 0.2)",
  border: "1px solid #f44336",
  width: "100%",
};

const successTitleStyle: React.CSSProperties = {
  fontWeight: "bold",
  color: "#4CAF50",
  marginBottom: "8px",
};

const errorTitleStyle: React.CSSProperties = {
  fontWeight: "bold",
  color: "#f44336",
  marginBottom: "8px",
};

const preStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--muted-text-gray)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: "200px",
  overflowY: "auto",
};

const formSectionStyle: React.CSSProperties = {
  width: "100%",
  marginBottom: "24px",
};

const dividerStyle: React.CSSProperties = {
  width: "100%",
  height: "1px",
  backgroundColor: "#4b4b4b",
  margin: "24px 0",
};

const errorTextStyle: React.CSSProperties = {
  marginTop: "4px",
  fontSize: "12px",
  color: "#f44336",
};

export default function EmployeeBulkCreateForm() {
  const { t } = useTranslation("employees");
  const [successData, setSuccessData] = useState(null);
  const [errorData, setErrorData] = useState<ApiErrorResponse | string | null>(null);
  const [resetSuccess, setResetSuccess] =
    useState<ResetPasswordsResponse | null>(null);
  const [resetError, setResetError] = useState<ApiErrorResponse | string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(uploadSchema),
  });

  const uploadMutation = useMutation({
    mutationFn: employeeService.bulkUploadEmployees,
    onSuccess: (data) => {
      setSuccessData(data);
      reset();
    },
    onError: (error: ApiError) => {
      setErrorData(error?.response?.data || "An error occurred.");
    },
  });

  const resetPasswordsMutation = useMutation({
    mutationFn: employeeService.resetPasswords,
    onSuccess: (data) => {
      setResetSuccess(data);
    },
    onError: (error: ApiError) => {
      setResetError(error?.response?.data || "An error occurred.");
    },
  });

  const onSubmit = (data: UploadFormSchema) => {
    const formData = new FormData();
    formData.append("file", data.file[0]);
    uploadMutation.mutate(formData);
  };

  const handleResetPasswords = () => {
    resetPasswordsMutation.mutate();
  };

  return (
    <div style={pageContainerStyle}>
      <RegisterHeader />
      <div style={pageContentStyle}>
        <div style={registrationWindowContainerStyle}>
          <div style={formCardStyle}>
            <h1 style={headerStyle}>{t("bulkUploadTitle")}</h1>

            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              <div style={formSectionStyle}>
                <label htmlFor="file" style={labelStyle}>
                  {t("bulkUploadExcelBtn")}
                </label>
                <input
                  type="file"
                  id="file"
                  {...register("file")}
                  style={errors.file ? inputErrorStyle : inputStyle}
                />
                {errors.file && (
                  <p style={errorTextStyle}>
                    {errors?.file?.message?.toString()}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={uploadMutation.isPending}
                style={
                  uploadMutation.isPending ? buttonDisabledStyle : buttonStyle
                }
              >
                {uploadMutation.isPending ? t("bulkUploadUploading") : t("bulkUploadBtn")}
              </button>
            </form>

            {uploadMutation.isSuccess && (
              <div style={successBoxStyle}>
                <h2 style={successTitleStyle}>{t("bulkUploadSuccess")}</h2>
                <pre style={preStyle}>
                  {JSON.stringify(successData, null, 2)}
                </pre>
              </div>
            )}

            {uploadMutation.isError && (
              <div style={errorBoxStyle}>
                <h2 style={errorTitleStyle}>{t("bulkUploadFailed")}</h2>
                <pre style={preStyle}>{JSON.stringify(errorData, null, 2)}</pre>
              </div>
            )}

            <div style={dividerStyle} />

            <div style={{ width: "100%" }}>
              <button
                onClick={handleResetPasswords}
                disabled={resetPasswordsMutation.isPending}
                style={
                  resetPasswordsMutation.isPending
                    ? {
                        ...secondaryButtonStyle,
                        opacity: 0.5,
                        cursor: "not-allowed",
                      }
                    : secondaryButtonStyle
                }
              >
                {resetPasswordsMutation.isPending
                  ? t("bulkResetPasswordsLoading")
                  : t("bulkResetPasswordsBtn")}
              </button>
            </div>

            {resetPasswordsMutation.isSuccess && (
              <div style={successBoxStyle}>
                <h2 style={successTitleStyle}>{t("bulkResetPasswordsSuccess")}</h2>
                <pre style={preStyle}>
                  {JSON.stringify(resetSuccess, null, 2)}
                </pre>
              </div>
            )}

            {resetPasswordsMutation.isError && (
              <div style={errorBoxStyle}>
                <h2 style={errorTitleStyle}>{t("bulkResetPasswordsFailed")}</h2>
                <pre style={preStyle}>
                  {JSON.stringify(resetError, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
